import React, { useCallback, useEffect, useState } from 'react';
import { auth } from '../firebase';
import { uploadModeratedMedia, mergeModerationTokens, normalizeModerationTokens } from '../utils/moderatedUpload';
import type { ModerationTokenMap } from '../utils/moderatedUpload';
import { checkUploadSize } from '../utils/mediaApi';
import { useParams, useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import SignedVideo from '../components/SignedVideo';
import { aiScanAd } from '../utils/moderation';
import { LOCATIONS, GENERAL_INTERESTS } from '../utils/locations';
import { AdBillingFields, AdCtaFields, AdPlacementFields, ResolvedImg } from '../components/AdFormFields';
import { AdBillingModel, validateAdCta } from '../utils/adActions';
import { getAdById, updateAd } from '../utils/secureFunctions';

const EditAd: React.FC = () => {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [currentImageURL, setCurrentImageURL] = useState('');
  const [currentVideoURL, setCurrentVideoURL] = useState('');
  const [existingModerationTokens, setExistingModerationTokens] = useState<ModerationTokenMap | null>(null);
  
  // Call to Action
  const [actionType, setActionType] = useState<'url' | 'call' | 'message' | 'whatsapp' | 'none'>('url');
  const [actionValue, setActionValue] = useState('');
  const [buttonText, setButtonText] = useState('Learn More');
  
  // Simplified targeting
  const [targetInterests, setTargetInterests] = useState<string[]>([]);
  const [targetGender, setTargetGender] = useState<'male' | 'female' | 'all'>('all');
  const [targetAgeMin, setTargetAgeMin] = useState(18);
  const [targetAgeMax, setTargetAgeMax] = useState(65);
  
  // Location targeting
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  
  // Placements & Budget
  const [placements, setPlacements] = useState<string[]>(['feed']);
  const [budget, setBudget] = useState(10);
  const [billingModel, setBillingModel] = useState<AdBillingModel>('cpm');


  const loadAd = useCallback(async () => {
    if (!adId) return;
    setLoading(true);
    try {
      const res = await getAdById(adId);
      const data = (res as any)?.ad;
      if (!data) {
        alert('Ad not found');
        navigate('/advertiser-dashboard');
        return;
      }
      
      // Check ownership
      if (data.createdBy !== auth.currentUser?.uid) {
        alert('You do not have permission to edit this ad');
        navigate('/advertiser-dashboard');
        return;
      }

      // Load existing values
      setTitle(data.title || '');
      setDescription(data.description || '');
      setCurrentImageURL(data.imageURL || '');
      setCurrentVideoURL(data.videoURL || '');
      setExistingModerationTokens(normalizeModerationTokens(data.moderationTokens) || null);
      setActionType(data.actionType || 'url');
      setActionValue(data.actionValue || data.targetURL || '');
      setButtonText(data.buttonText || 'Learn More');
      setTargetInterests(data.targetInterests || []);
      setTargetGender(data.targetGender || 'all');
      setTargetAgeMin(data.targetAgeMin || 18);
      setTargetAgeMax(data.targetAgeMax || 65);
      setPlacements(data.placements || ['feed']);
      
      // Load location targeting
      if (data.targetLocations) {
        // Load first country, state, city from arrays
        const country = data.targetLocations.countries?.[0] || '';
        setSelectedCountry(country);
        
        if (country && data.targetLocations.states?.[country]) {
          const state = data.targetLocations.states[country][0] || '';
          setSelectedState(state);
          
          if (state && data.targetLocations.cities?.[country]?.[state]) {
            const city = data.targetLocations.cities[country][state][0] || '';
            setSelectedCity(city);
          }
        }
      }
      
      // Budgets are stored and billed in USD.
      setBudget(Number(data.budget) || 10);
      setBillingModel(data.billingModel === 'cpc' ? 'cpc' : 'cpm');
    } catch (error) {
      console.error('Failed to load ad:', error);
      alert('Failed to load ad');
    } finally {
      setLoading(false);
    }
  }, [adId, navigate]);

  useEffect(() => {
    if (!adId) return;
    loadAd();
  }, [adId, loadAd]);

  const handleInterestToggle = (interest: string) => {
    if (targetInterests.includes(interest)) {
      setTargetInterests(prev => prev.filter(i => i !== interest));
    } else {
      setTargetInterests(prev => [...prev, interest]);
    }
  };


  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!auth.currentUser || !adId) {
      alert('Please log in');
      return;
    }

    if (placements.length === 0) {
      alert('Please choose at least one place to show your ad.');
      return;
    }

    const ctaProblem = validateAdCta(actionType, actionValue);
    if (ctaProblem) {
      alert(ctaProblem);
      return;
    }

    // Make location compulsory
    if (!selectedCountry) {
      alert('Please select at least a country for targeting');
      return;
    }

    setSaving(true);

    try {
      let imageURL = currentImageURL;
      let videoURL = currentVideoURL;
      let moderationTokens: ModerationTokenMap | undefined = existingModerationTokens ? { ...existingModerationTokens } : undefined;

      // Upload new image if provided
      if (imageFile) {
        const upload = await uploadModeratedMedia({
          folder: 'ads',
          field: 'imageURL',
          file: imageFile,
          contentType: imageFile.type || 'image/jpeg',
        });
        imageURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      if (videoFile) {
        const upload = await uploadModeratedMedia({
          folder: 'ads',
          field: 'videoURL',
          file: videoFile,
          contentType: videoFile.type || 'video/mp4',
        });
        videoURL = upload.objectKey;
        moderationTokens = mergeModerationTokens(moderationTokens, upload);
      }

      // Re-scan
      const scan = await aiScanAd({ 
        title, 
        description, 
        targetURL: actionType === 'url' ? actionValue : undefined, 
        imageURL, 
        videoURL 
      });

      // Flatten location data
      const locationTargets = {
        countries: selectedCountry ? [selectedCountry] : [],
        states: selectedCountry && selectedState ? { [selectedCountry]: [selectedState] } : {},
        cities: selectedCountry && selectedState && selectedCity ? { [selectedCountry]: { [selectedState]: [selectedCity] } } : {}
      };

      // Normalize budget to USD
      const budgetUSD = budget;

      // Update ad document - only include defined fields
      const updateData: any = {
        title,
        description,
        imageURL,
        videoURL,
        actionType,
        actionValue,
        targetGender,
        targetLocations: locationTargets,
        targetAgeMin,
        targetAgeMax,
        placements: placements as any[],
        budgetUsd: budgetUSD,
        billingModel,
        aiScan: { result: scan.result, reasons: scan.reasons, mode: 'client' as const, scannedAt: new Date().toISOString() },
        moderationTokens: moderationTokens || null,
      };

      // Only add optional fields if they have values
      if (actionType === 'url' && actionValue) {
        updateData.targetURL = actionValue;
      }
      if (actionType !== 'none' && buttonText) {
        updateData.buttonText = buttonText;
      }
      if (targetInterests.length > 0) {
        updateData.targetInterests = targetInterests;
      }

      // If content flagged, set to pending review
      if (scan.result !== 'clean') {
        updateData.status = 'pending_approval';
        updateData.reviewStatus = 'needs_review';
      }

      await updateAd(adId, updateData);
      setExistingModerationTokens(moderationTokens || null);

      alert(scan.result === 'clean' ? 'Ad updated successfully!' : 'Ad updated. Our safety check may need a quick manual look before it runs again.');
      navigate('/advertiser-dashboard');
    } catch (error) {
      console.error('Failed to update ad:', error);
      alert('Failed to update ad. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <TopNavigation />
        <div style={styles.content}>
          <p>Loading ad...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TopNavigation />
      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Edit Advertisement</h1>
            <p style={styles.subtitle}>Update your ad details and targeting</p>
          </div>
          <button onClick={() => navigate('/advertiser-dashboard')} style={styles.backBtn}>
            ← Back to Dashboard
          </button>
        </div>

        <form onSubmit={handleSave} style={styles.form}>
          {/* Basic Info */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Ad Details</h3>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                style={styles.input}
                placeholder="Your ad title"
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                style={styles.textarea}
                placeholder="Describe your ad"
                rows={3}
              />
            </div>

            <AdCtaFields
              actionType={actionType}
              actionValue={actionValue}
              buttonText={buttonText}
              onChange={(n) => { setActionType(n.actionType); setActionValue(n.actionValue); setButtonText(n.buttonText); }}
              styles={styles}
            />

            <div style={styles.formGroup}>
              <label style={styles.label}>Ad Image {currentImageURL && '(current image set)'}</label>
              {currentImageURL && (
                <ResolvedImg src={currentImageURL} alt="Current" style={styles.previewImage} />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                style={styles.fileInput}
              />
              <p style={styles.hint}>Upload a new image to replace the current one</p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Ad Video {currentVideoURL && '(current video set)'}</label>
              {currentVideoURL && (
                <SignedVideo src={currentVideoURL} style={styles.previewImage} controls />
              )}
              <input
                type="file"
                accept="video/*"
                onChange={(e) => { const f = e.target.files?.[0] || null; if (f && !checkUploadSize(f)) { e.target.value = ''; return; } setVideoFile(f); }}
                style={styles.fileInput}
              />
              <p style={styles.hint}>Upload a new video to replace the current one</p>
            </div>
          </div>

          {/* Audience Targeting */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Target Audience</h3>

            <div style={styles.formGroup}>
              <label style={styles.label}>Age Range: {targetAgeMin} - {targetAgeMax}</label>
              <div style={styles.ageInputs}>
                <div>
                  <label style={styles.smallLabel}>Min Age</label>
                  <input
                    type="number"
                    min="13"
                    max="100"
                    value={targetAgeMin}
                    onChange={(e) => setTargetAgeMin(Number(e.target.value))}
                    style={styles.ageInput}
                  />
                </div>
                <span style={{alignSelf: 'flex-end', padding: '8px'}}>to</span>
                <div>
                  <label style={styles.smallLabel}>Max Age</label>
                  <input
                    type="number"
                    min="13"
                    max="100"
                    value={targetAgeMax}
                    onChange={(e) => setTargetAgeMax(Number(e.target.value))}
                    style={styles.ageInput}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Gender</label>
              <select value={targetGender} onChange={(e) => setTargetGender(e.target.value as any)} style={styles.input}>
                <option value="all">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Interests</label>
              <div style={styles.checkboxGrid}>
                {GENERAL_INTERESTS.map(interest => (
                  <label key={interest} style={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={targetInterests.includes(interest)}
                      onChange={() => handleInterestToggle(interest)}
                    />
                    {interest}
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Location Targeting */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Location Targeting</h3>
            <p style={styles.hint}>Select country, then state and city for precise targeting</p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Country</label>
              <select
                value={selectedCountry}
                onChange={e => {
                  setSelectedCountry(e.target.value);
                  setSelectedState('');
                  setSelectedCity('');
                }}
                style={styles.input}
              >
                <option value="">Select Country</option>
                {LOCATIONS.map(loc => (
                  <option key={loc.countryCode} value={loc.countryCode}>{loc.country}</option>
                ))}
              </select>
            </div>

            {selectedCountry && (
              <div style={styles.formGroup}>
                <label style={styles.label}>State/Province</label>
                <select
                  value={selectedState}
                  onChange={e => {
                    setSelectedState(e.target.value);
                    setSelectedCity('');
                  }}
                  style={styles.input}
                >
                  <option value="">Select State/Province</option>
                  {LOCATIONS.find(loc => loc.countryCode === selectedCountry)?.states?.map(state => (
                    <option key={state.name} value={state.name}>{state.name}</option>
                  ))}
                </select>
              </div>
            )}

            {selectedCountry && selectedState && (
              <div style={styles.formGroup}>
                <label style={styles.label}>City/Town</label>
                <select
                  value={selectedCity}
                  onChange={e => setSelectedCity(e.target.value)}
                  style={styles.input}
                >
                  <option value="">Select City/Town</option>
                  {LOCATIONS.find(loc => loc.countryCode === selectedCountry)?.states?.find(s => s.name === selectedState)?.cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Placements */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Where to Show Your Ad</h3>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 8px' }}>Your ad is only shown in the places you tick, to people who match your audience.</p>
            <AdPlacementFields placements={placements} onChange={setPlacements} />
          </div>

          {/* Budget */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Budget</h3>
            <AdBillingFields value={billingModel} onChange={setBillingModel} budgetUsd={budget} />

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Total Campaign Budget: ${budget.toLocaleString()} USD
              </label>
              <input
                type="range"
                min={10}
                max={10000}
                step={10}
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                style={styles.range}
              />
              <input
                type="number"
                min={10}
                aria-label="Budget in USD"
                value={budget}
                onChange={(e) => setBudget(Number(e.target.value))}
                style={{ ...styles.input, maxWidth: 200, marginTop: 8 }}
              />
            </div>
          </div>

          <div style={styles.actions}>
            <button type="button" onClick={() => navigate('/advertiser-dashboard')} style={styles.cancelBtn}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={styles.submitBtn}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  content: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#072B2C',
    margin: 0,
  },
  subtitle: {
    fontSize: 14,
    color: '#5B6E6E',
    marginTop: 4,
  },
  backBtn: {
    padding: '10px 16px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
  },
  form: {
    background: '#fff',
    borderRadius: 12,
    padding: 32,
  },
  section: {
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#072B2C',
    marginBottom: 16,
    borderBottom: '2px solid #66EAD1',
    paddingBottom: 8,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: '500',
    color: '#072B2C',
    marginBottom: 8,
  },
  smallLabel: {
    display: 'block',
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  ageInput: {
    width: 100,
    padding: 10,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
  },
  ageInputs: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-end',
  },
  textarea: {
    width: '100%',
    padding: 12,
    borderRadius: 8,
    border: '1px solid #ccc',
    fontSize: 14,
    resize: 'vertical',
  },
  fileInput: {
    width: '100%',
    padding: 8,
    fontSize: 14,
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  previewImage: {
    maxWidth: 300,
    maxHeight: 200,
    borderRadius: 8,
    marginBottom: 12,
    objectFit: 'cover',
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    cursor: 'pointer',
  },
  locationBlock: {
    marginBottom: 20,
    padding: 16,
    background: '#f9f9f9',
    borderRadius: 8,
  },
  countryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    cursor: 'pointer',
    marginBottom: 12,
  },
  statesContainer: {
    paddingLeft: 24,
  },
  stateBlock: {
    marginBottom: 12,
  },
  stateLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    fontWeight: '500',
    cursor: 'pointer',
    marginBottom: 8,
  },
  citiesContainer: {
    paddingLeft: 24,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: 8,
  },
  cityLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
  range: {
    width: '100%',
    marginTop: 8,
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '12px 24px',
    background: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: '600',
  },
  submitBtn: {
    padding: '12px 24px',
    background: '#0B6B6F',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: '600',
  },
};

export default EditAd;
